import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    Checkbox,
    FormField,
    Heading,
    Input,
    Label,
    Option,
    Select,
    SelectList,
    Spinner,
    Text,
    TextLink,
} from "@indeed/ifl-components"
import { ArrowRight } from "@indeed/ifl-icons"
import { toastError } from "../../../common/components/indeed-toast-container"
import { SingleColumnPage } from "../../../common/components/single-column-page"
import { configSelector } from "../../../common/utils/config-slice"
import { useAppDispatch, useAppSelector } from "../../../common/utils/redux-helpers"
import { ReqStatus } from "../../../common/utils/utils"
import { fetchChromaticUrl, resetDislikePreferences } from "./test-hub-api"
import { TestHubInfoConverter } from "./test-hub-info-converter"
import { chromaticSelector } from "./test-hub-slices"

export const TestHubPage: React.FC = () => {
    const config = useAppSelector(configSelector)
    const chromaticUrl = useAppSelector(chromaticSelector)
    const testUsers: { accountId: number; archetype: string }[] = [
        { accountId: 0, archetype: "Myself (No Override)" },
        { accountId: 1185754796, archetype: "Shop Manager" },
        { accountId: 1186038580, archetype: "Receptionist" },
        { accountId: 1186037564, archetype: "Customer Service Representative" },
        { accountId: 1186037791, archetype: "Delivery Driver" },
        { accountId: 1186038050, archetype: "Truck Driver" },
        { accountId: 1186038193, archetype: "Security Officer" },
        { accountId: 1186038277, archetype: "Server" },
        { accountId: 1186038410, archetype: "Sales Associate" },
        { accountId: 1186038480, archetype: "Nursing Assistant" },
        { accountId: 1186038780, archetype: "Store Manager" },
        { accountId: 1186038927, archetype: "Bartender" },
        { accountId: 1175215019, archetype: "Johnny Tester" },
        { accountId: 6375342384, archetype: "64-bit Account" },
    ]
    // Keeping parity with extractLocales from build.gradle
    // prettier-ignore
    const extractLocales = "ar ar_AE ar_BH ar_EG ar_KW ar_MA ar_OM ar_QA ar_SA cs cs_CZ da da_DK de de_AT de_BE de_CH de_DE de_LU el el_GR en en_AE en_AU en_CA en_IN en_ID en_IE en_HK en_GB en_MA en_MY en_NG en_NZ en_PH en_PK en_QA en_SA en_SG en_US en_VN en_ZA es es_AR es_CL es_CO es_CR es_EC es_ES es_MX es_PA es_PE es_US es_UY es_VE fi fi_FI fr fr_BE fr_CA fr_CH fr_FR fr_LU fr_MA he he_IL hi hi_IN hu hu_HU id id_ID it it_CH it_IT ja ko ko_KR ms nl nl_BE nl_NL no no_NO pl pl_PL pt pt_BR pt_PT ro ro_RO ru ru_RU ru_UA sv sv_FI sv_SE th th_TH tr tr_TR uk uk_UA vi vi_VN zh zh_CN zh_HK zh_SG zh_TW"
    const locales: string[] = Array.from(new Set(extractLocales.split(" "))).sort((a, b) => a.localeCompare(b))
    const pages: { name: string; url: string; additionalParams?: { [key: string]: string }[] }[] = [
        {
            name: "Explicit Feedback",
            url: "/private/debug/random-feedback-page",
            additionalParams: [
                {
                    key: "explicitCountries",
                    param: "countries",
                    displayText: "Countries (Defaults US,CA,GB)",
                },
            ],
        },
        {
            name: "Feedback Limit",
            url: "/invitations/feedback/limit",
        },
        {
            name: "Pause",
            url: "/private/debug/test-pause-page",
            additionalParams: [
                {
                    key: "pauseCountries",
                    param: "countries",
                    displayText: "Countries (Defaults US,CA,GB)",
                },
                {
                    key: "pauseNumJobIds",
                    param: "numOfJobIds",
                    displayText: "Number of JobIds to fetch (Defaults to 10)",
                },
            ],
        },
        {
            name: "Rel Rating",
            url: "/private/debug/test-match-rating",
            additionalParams: [
                {
                    key: "rating",
                    param: "Rating",
                    displayText: "Rating (1-5)",
                },
            ],
        },
    ]

    const [currentTestUser, setCurrentTestUser] = useState(testUsers[0].accountId)
    const [currentPageSelection, setCurrentPageSelection] = useState(pages[0].url)
    const [persistQaLogin, setPersistQaLogin] = useState(true)
    const [currentAdditionalParams, setCurrentAdditionalParams] = useState({} as { [key: string]: string })
    const [resetBtn, setResetBtn] = useState(true)
    const [currentLocale, setCurrentLocale] = useState<string | null>(null)
    const dispatch = useAppDispatch()

    function getCurrentPage(page = currentPageSelection) {
        return pages.find(({ url }) => url === page)
    }

    const currentPage = getCurrentPage()

    const handleOnClick = () => {
        window.open(buildUrl(), "_self")
    }

    const handleResetDislikePreferences = async () => {
        const response = await dispatch(resetDislikePreferences()).unwrap()

        if (response === ReqStatus.SUCCESS) setResetBtn(false)
        else toastError("Could not reset dislike preferences at the moment, please try again later")
    }

    const onUserChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentTestUser(parseInt(event.currentTarget.value, 10))
    }

    const onPageSelectionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentPageSelection(event.currentTarget.value)
        setCurrentAdditionalParams(defaultFromAdditionalOptions(getCurrentPage(event.currentTarget.value)?.additionalParams))
    }

    const onPersistQaLoginChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
        setPersistQaLogin(event.currentTarget.checked)
    }

    const onAdditionalParams = (event: React.SyntheticEvent<HTMLInputElement>, option: string) => {
        setCurrentAdditionalParams({
            ...currentAdditionalParams,
            [option]: event.currentTarget.value,
        })
    }

    function defaultFromAdditionalOptions(additionalParams?: { [key: string]: string }[]) {
        if (!additionalParams) {
            return {}
        }
        return Object.fromEntries(additionalParams.map(({ param }) => [param, ""]))
    }

    function buildUrl() {
        const path = currentPageSelection
        const paramMap = {
            overrideAccountId: currentTestUser,
            persistQaLogin,
            hl: currentLocale,
            ...currentAdditionalParams,
        }
        return `${path}?${Object.entries(paramMap)
            .filter(([key, value]) => value)
            .map(([key, value]) => `${key}=${value}`)
            .join("&")}`
    }

    useEffect(() => {
        dispatch(fetchChromaticUrl())
    })

    return (
        <SingleColumnPage>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    paddingBlockEnd: 8,
                    paddingBlockStart: 8,
                    paddingInlineEnd: 8,
                    paddingInlineStart: 8,
                    maxInlineSize: "50rem",
                    marginBlockEnd: 0,
                    marginBlockStart: 0,
                    marginInlineStart: "auto",
                    marginInlineEnd: "auto",
                }}
            >
                <Heading as="h1" level={5}>
                    JSX Test Hub
                </Heading>
                <Text>
                    Link to{" "}
                    {chromaticUrl.data ? (
                        <TextLink target="_blank" href={chromaticUrl.data}>
                            Chromatic/Storybook
                        </TextLink>
                    ) : (
                        <Spinner title="loading" />
                    )}
                </Text>
                <Text as="h2" level={1}>
                    Launching a test will authenticate you as the selected user in QA/local and redirect you to the selected page.
                </Text>
                <TestHubInfoConverter />
                <Label sx={{ paddingBlockStart: 3 }} htmlFor="jobTitle">
                    Select A Job Title
                </Label>
                <Select value={currentTestUser} onChange={onUserChange} id="jobTitle">
                    {testUsers.map((user) => (
                        <option key={user.accountId} value={user.accountId}>
                            {user.archetype}
                        </option>
                    ))}
                </Select>
                <Label sx={{ paddingBlockStart: 3 }} htmlFor="pageName">
                    Select a page
                </Label>
                <Select value={currentPageSelection} onChange={onPageSelectionChange} id="pageName" data-testid="page-type-selector">
                    {pages.map((page) => (
                        <option key={page.url} value={page.url}>
                            {page.name}
                        </option>
                    ))}
                </Select>
                <FormField
                    sx={{ paddingBlockStart: 3, inlineSize: "100%", display: "inherit", flexDirection: "inherit", alignItems: "stretch" }}
                    label="Optional: Select a Locale"
                    labelProps={{
                        style: {
                            alignSelf: "center",
                        },
                    }}
                >
                    <SelectList
                        data-testid="locale-select-list"
                        onSelectionChange={(selectedItem) => setCurrentLocale(selectedItem[0])}
                        placeholder="Select a locale"
                    >
                        {locales.map((locale) => (
                            <Option key={`${locale}`} value={locale}>
                                {locale}
                            </Option>
                        ))}
                    </SelectList>
                </FormField>
                {currentPage?.additionalParams &&
                    currentPage.additionalParams.map(({ key, param, displayText }) => (
                        <FormField
                            key={key}
                            sx={{
                                paddingBlockStart: 3,
                                inlineSize: "100%",
                                display: "inherit",
                                flexDirection: "inherit",
                                alignItems: "stretch",
                            }}
                        >
                            <Label sx={{ alignSelf: "center" }} data-testid="additional-params-label">
                                Input Optional Param: {displayText}
                            </Label>
                            <Input
                                aria-label={displayText}
                                inputMode="text"
                                onChange={(event) => onAdditionalParams(event, param)}
                                value={currentAdditionalParams[param]}
                                data-testid="additional-params-input"
                            />
                        </FormField>
                    ))}
                {config.env === "QA" && (
                    <Checkbox
                        sx={{ paddingBlockStart: 6 }}
                        label="Persist QA Login"
                        defaultChecked={persistQaLogin}
                        onChange={onPersistQaLoginChange}
                    />
                )}
            </Box>
            <Box
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Button size="md" iconAfter={<ArrowRight />} onClick={handleOnClick} variant="primary" type="button">
                    Launch {currentPage?.name} with {testUsers.find(({ accountId }) => accountId === currentTestUser)?.archetype}
                </Button>
            </Box>
            <Box
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {resetBtn ? (
                    <Button size="md" iconAfter={<ArrowRight />} onClick={handleResetDislikePreferences} variant="text" type="button">
                        Reset dislike preferences
                    </Button>
                ) : (
                    <Text> Preferences Successfully Reset </Text>
                )}
            </Box>
        </SingleColumnPage>
    )
}
